import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

const IMAGE_SIZE = {
  xs: "h-8  w-8",
  sm: "h-10 w-10",
  md: "h-14 w-14",
  lg: "h-18 w-18",
  xl: "h-22 w-22",
} as const;

const TEXT_NAME_SIZE = {
  xs: "text-sm",
  sm: "text-base",
  md: "text-lg",
  lg: "text-xl",
  xl: "text-2xl",
} as const;

const TEXT_SUB_SIZE = {
  xs: "text-[7px]  tracking-[0.10em]",
  sm: "text-[8px]  tracking-[0.12em]",
  md: "text-[9px]  tracking-[0.15em]",
  lg: "text-[10px] tracking-[0.18em]",
  xl: "text-[11px] tracking-[0.20em]",
} as const;

const VARIANT_NAME: Record<LogoVariant, string> = {
  default: "text-[#1A1A1A]",
  light: "text-white",
  dark: "text-white",
};

const VARIANT_SUB: Record<LogoVariant, string> = {
  default: "text-[#C9A84C]",
  light: "text-[#C9A84C]",
  dark: "text-[#C9A84C]",
};

const GAP = {
  xs: "gap-1.5",
  sm: "gap-2",
  md: "gap-2.5",
  lg: "gap-3",
  xl: "gap-3.5",
} as const;

export type LogoVariant = "default" | "light" | "dark";
export type LogoSize = keyof typeof IMAGE_SIZE;
export type LogoMode = "full" | "mark" | "wordmark";

interface BaseLogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  imageSize?: LogoSize;
  mode?: LogoMode;
  asLink?: boolean;
  imageSrc?: string;
  name?: string;
  subtitle?: string;
  hideSubtitle?: boolean;
  className?: string;
  imageClassName?: string;
  nameClassName?: string;
  subtitleClassName?: string;
  onClick?: () => void;
}

function LogoMark({
  size = "md",
  imageSrc = "/images/Rosewood Logo main version.png",
  imageClassName,
}: Pick<BaseLogoProps, "size" | "imageSrc" | "imageClassName">) {
  return (
    <img
      src={imageSrc}
      alt="Rosewood Tax Consultancy logo"
      className={cn(
        "object-contain shrink-0",
        IMAGE_SIZE[size!],
        imageClassName,
      )}
    />
  );
}

function LogoWordmark({
  variant = "default",
  size = "md",
  name = "Rosewood",
  subtitle = "Consultancy",
  hideSubtitle = false,
  nameClassName,
  subtitleClassName,
}: Pick<
  BaseLogoProps,
  | "variant"
  | "size"
  | "name"
  | "subtitle"
  | "hideSubtitle"
  | "nameClassName"
  | "subtitleClassName"
>) {
  return (
    <div className="flex flex-col leading-none">
      <span
        className={cn(
          " font-semibold leading-none",
          TEXT_NAME_SIZE[size!],
          VARIANT_NAME[variant!],
          nameClassName,
        )}
      >
        {name}
      </span>
      {!hideSubtitle && (
        <span
          className={cn(
            "font-medium uppercase mt-[3px]",
            TEXT_SUB_SIZE[size!],
            VARIANT_SUB[variant!],
            subtitleClassName,
          )}
        >
          {subtitle}
        </span>
      )}
    </div>
  );
}

function LogoContent(props: BaseLogoProps) {
  const {
    variant = "default",
    size = "md",
    imageSize,
    mode = "full",
    imageSrc,
    name,
    subtitle,
    hideSubtitle,
    imageClassName,
    nameClassName,
    subtitleClassName,
  } = props;

  const markSize = imageSize ?? size;

  if (mode === "mark") {
    return (
      <LogoMark
        size={markSize}
        imageSrc={imageSrc}
        imageClassName={imageClassName}
      />
    );
  }

  if (mode === "wordmark") {
    return (
      <LogoWordmark
        variant={variant}
        size={size}
        name={name}
        subtitle={subtitle}
        hideSubtitle={hideSubtitle}
        nameClassName={nameClassName}
        subtitleClassName={subtitleClassName}
      />
    );
  }

  return (
    <div className={cn("flex items-center", GAP[size!])}>
      <LogoMark
        size={markSize}
        imageSrc={imageSrc}
        imageClassName={imageClassName}
      />
      <LogoWordmark
        variant={variant}
        size={size}
        name={name}
        subtitle={subtitle}
        hideSubtitle={hideSubtitle}
        nameClassName={nameClassName}
        subtitleClassName={subtitleClassName}
      />
    </div>
  );
}

export function Logo({
  asLink = true,
  className,
  onClick,
  ...rest
}: BaseLogoProps) {
  const inner = <LogoContent {...rest} />;

  if (asLink) {
    return (
      <Link
        to="/"
        onClick={onClick}
        aria-label="Rosewood Tax Consultancy — home"
        className={cn(
          "inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C] rounded-sm",
          className,
        )}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      onClick={onClick}
      className={cn("inline-flex items-center", className)}
    >
      {inner}
    </div>
  );
}
